import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, MouseEvent } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import Checkbox from '@/components/Forms/Checkbox';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import { CompanyResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

// Printed as the buyer block on invoice PDFs, in this order.
const INVOICE_DETAIL_KEYS = [
  'vat_code',
  'address',
  'city',
  'post_code',
  'country',
] as const;

type InvoiceDetailKey = (typeof INVOICE_DETAIL_KEYS)[number];

type FormData = {
  name: string;
  short: string;
  registration_code: string;
  vat_code: string;
  address: string;
  city: string;
  post_code: string;
  country: string;
  bank_account: {
    bank_name: string;
    iban: string;
    bic: string;
  };
  hide_from_dropdowns: boolean;
};

const BlockHeading = styled(Text)`
  grid-column: span 2;
  margin-bottom: -8px;
`;

interface Props {
  company?: CompanyResource;
  onClose: () => void;
}

const CompanyFormModal: FC<Props> = ({ company, onClose }) => {
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  // The backend only returns registration_code / bank_account to these users,
  // so anyone else neither sees nor submits them.
  const canSeeFinanceDetails = useHasPermission(
    'manage organisation settings',
    'manage invoices',
  );

  // Until the backend migration runs (or when the user lacks visibility) the
  // keys are missing entirely. Only show and submit them when they came back,
  // otherwise saving would wipe values this form never loaded.
  const canEditInvoiceDetails =
    canSeeFinanceDetails &&
    (!company || INVOICE_DETAIL_KEYS.some((key) => key in company));

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      name: company?.name || '',
      short: company?.short || '',
      registration_code: company?.registration_code || '',
      vat_code: company?.vat_code || '',
      address: company?.address || '',
      city: company?.city || '',
      post_code: company?.post_code || '',
      country: company?.country || '',
      bank_account: {
        bank_name: company?.bank_account?.bank_name || '',
        iban: company?.bank_account?.iban || '',
        bic: company?.bank_account?.bic || '',
      },
      hide_from_dropdowns: company?.hide_from_dropdowns || false,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (formData) => {
      // Existing companies predate `short`, so editing one now has to supply it.
      if (!formData.short.trim()) {
        setError('short', { message: 'Short code is required' });
        return;
      }

      const bankName = formData.bank_account.bank_name.trim();
      const iban = formData.bank_account.iban.trim();
      const bic = formData.bank_account.bic.trim();
      const filledBankFields = [bankName, iban, bic].filter(Boolean).length;

      // All three go together: either a complete account or none at all.
      if (
        canSeeFinanceDetails &&
        filledBankFields > 0 &&
        filledBankFields < 3
      ) {
        if (!bankName) {
          setError('bank_account.bank_name', {
            message: 'Bank name is required',
          });
        }
        if (!iban) {
          setError('bank_account.iban', { message: 'IBAN is required' });
        }
        if (!bic) {
          setError('bank_account.bic', { message: 'BIC is required' });
        }
        return;
      }

      try {
        const data: Record<string, unknown> = {
          name: formData.name,
          short: formData.short,
          hide_from_dropdowns: formData.hide_from_dropdowns,
        };

        // Omitting the keys leaves the stored values untouched, which is what
        // we want when this user was never shown them in the first place.
        if (canSeeFinanceDetails) {
          data.registration_code = formData.registration_code.trim() || null;
          data.bank_account = filledBankFields
            ? { bank_name: bankName, iban, bic }
            : null;
        }

        // Sent as typed: the backend prints these verbatim. Empty clears.
        if (canEditInvoiceDetails) {
          INVOICE_DETAIL_KEYS.forEach((key: InvoiceDetailKey) => {
            data[key] = formData[key].trim() ? formData[key] : null;
          });
        }

        if (company) {
          await api.put<typeof data, CompanyResource>(
            `/v1/companies/${company.id}`,
            data,
          );
        } else {
          await api.post<typeof data, CompanyResource>('/v1/companies', data);
        }

        await queryClient.invalidateQueries(['companies'], {
          exact: false,
        });

        onClose();
        alerts.success(
          `Company was successfully ${company ? 'updated' : 'created'}`,
        );
      } catch (error) {
        // ignoreSplit keeps the nested `bank_account.iban` style keys intact so
        // they land on the right field instead of collapsing to `bank_account`.
        handleFormErrors<FormData>(error, setError, alerts, true);
      }
    })(e);
  };

  const onCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <Modal>
      <SidePanel
        heading={`${company ? 'Edit' : 'Add'} Company`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormData>
                control={control}
                name="name"
                label="Name"
                id="nameInput"
                placeholder="Add company name"
              />
              <FormField<FormData>
                control={control}
                name="short"
                label="Short"
                id="shortInput"
                placeholder="Add short code"
              />
              {canSeeFinanceDetails && (
                <>
                  <FormField<FormData>
                    control={control}
                    name="registration_code"
                    label="Registration Code"
                    id="registrationCodeInput"
                    placeholder="Add registration code"
                    optional
                  />
                  {canEditInvoiceDetails && (
                    <>
                      <BlockHeading $type="caption">
                        Invoice details
                      </BlockHeading>
                      <FormField<FormData>
                        control={control}
                        name="vat_code"
                        label="VAT Code"
                        id="vatCodeInput"
                        placeholder="e.g. LT100009206014"
                        optional
                      />
                      <FormField<FormData>
                        control={control}
                        name="address"
                        label="Address"
                        id="addressInput"
                        placeholder="e.g. Antakalnio g. 17-12"
                        optional
                      />
                      <FormField<FormData>
                        control={control}
                        name="city"
                        label="City"
                        id="cityInput"
                        placeholder="e.g. Vilnius"
                        optional
                      />
                      <FormField<FormData>
                        control={control}
                        name="post_code"
                        label="Post Code"
                        id="postCodeInput"
                        placeholder="e.g. LT-10312"
                        optional
                      />
                      <FormField<FormData>
                        control={control}
                        name="country"
                        label="Country"
                        id="countryInput"
                        placeholder="e.g. Lietuva"
                        optional
                      />
                    </>
                  )}
                  <BlockHeading $type="caption">Bank account</BlockHeading>
                  <FormField<FormData>
                    control={control}
                    name="bank_account.bank_name"
                    label="Bank Name"
                    id="bankNameInput"
                    placeholder="Add bank name"
                  />
                  <FormField<FormData>
                    control={control}
                    name="bank_account.iban"
                    label="IBAN"
                    id="ibanInput"
                    placeholder="Add IBAN"
                  />
                  <FormField<FormData>
                    control={control}
                    name="bank_account.bic"
                    label="BIC"
                    id="bicInput"
                    placeholder="Add BIC"
                  />
                </>
              )}
              <Larger>
                <Checkbox control={control} name="hide_from_dropdowns">
                  Hide from dropdowns
                </Checkbox>
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {company ? 'Update' : 'Add'}
            </Button>
            <Button color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default CompanyFormModal;
